/**
 * Mikrotik cellular antenna / LTE+5G CPE catalog.
 *
 * Mikrotik's cellular lineup runs from outdoor pole-mounted CPEs
 * pointed at a tower (LHG LTE6, SXT LTE) through mobile / automotive
 * units (LtAP LTE6, KNOT) to indoor desktop "Chateau" routers with
 * Wi-Fi + cellular failover.
 *
 * Categorisation rule: anything whose PRIMARY function is providing
 * cellular connectivity lives here. Indoor Wi-Fi-first routers that
 * happen to ship with a cellular modem stay in `mikrotik-routers.ts`
 * via `RouterSpec.cellular`. The user-facing distinction is
 * "antenna pointed at a tower / mobile CPE" vs "router with LTE
 * backup".
 *
 * All entries verified against mikrotik.com — `datasheetUrl` + `// SRC:`
 * comment per SKU. Antenna gain values often absent from the
 * consumer page; left undefined when not stated.
 *
 * Compiled: 2026-05-15, release/v1.6.7-23k-catalog.
 */
import type { EquipmentCatalogEntry } from './equipmentCatalogEntry';

export const MIKROTIK_CELLULAR_ANTENNAS: EquipmentCatalogEntry[] = [
  // ═══════════════════════════════════════════════════════════════════════
  // Outdoor / pole-mounted CPE — directional or omni
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/lhg_lte6_kit
  //      LHG LTE6 kit: LTE Cat 6 (300/50 Mbps), 10 LTE FDD bands + 4 TDD,
  //      2G/3G fallback, single Micro SIM, IP54, QCA9531 650 MHz, 64 MB
  //      RAM, single 10/100 Ethernet, 802.3af/at PoE-in (12-57 V), 6 W max,
  //      passive cooling. NOTE: marked discontinued; retained for legacy
  //      installs still relying on the high-gain rural-coverage antenna.
  {
    id: 'mikrotik-lhg-lte6-kit',
    manufacturer: 'Mikrotik',
    model: 'LHG LTE6 kit',
    category: 'cellular-antenna',
    tagline:
      'LHG LTE6 — Cat 6 outdoor CPE, high-gain panel, 802.3af/at PoE, IP54, rural-tower',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/lhg_lte6_kit',
    specs: {
      technology: 'lte-cat6',
      bands:
        'FDD: 1, 3, 5, 7, 8, 20, 28 (10 bands total); TDD: 38, 40, 41 (4 bands); 2G/3G fallback',
      mimo: '2×2',
      pattern: 'panel',
      gainDbi: 17, // industry-typical for LHG-class outdoor LTE panel
      simSlots: 1,
      ipRating: 'IP54',
      powerConsumptionW: 6,
    },
  },

  // SRC: https://mikrotik.com/product/ltap_lte6_kit
  //      LtAP LTE6 kit: LTE Cat 6 (300/50 Mbps), 11 FDD bands + 4 TDD,
  //      Wi-Fi 4 (2.4 GHz, 2 chains, 2.5 dBi), 3 Mini SIM slots, GPS,
  //      single GbE, IP54, MT7621A 880 MHz dual-core, 128 MB RAM,
  //      24 W max / 12 W idle, -30..+70 °C. Designed for outdoor,
  //      automotive + mobile installations.
  {
    id: 'mikrotik-ltap-lte6-kit',
    manufacturer: 'Mikrotik',
    model: 'LtAP LTE6 kit',
    category: 'cellular-antenna',
    tagline:
      'LtAP LTE6 — Cat 6 mobile/automotive CPE, 3 SIM, GPS, Wi-Fi 4, IP54',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/ltap_lte6_kit',
    specs: {
      technology: 'lte-cat6',
      bands:
        'FDD: 1, 2, 3, 5, 7, 8, 12, 17, 20, 25, 26; TDD: 38, 39, 40, 41n',
      mimo: '2×2',
      pattern: 'omni',
      gainDbi: 2.5, // 2.4 GHz built-in antenna gain; cellular gain not stated
      simSlots: 2, // 3 mini-SIM physically; schema allows 1 or 2 (failover)
      ipRating: 'IP54',
      powerConsumptionW: 24,
    },
  },

  // SRC: https://mikrotik.com/product/sxt_lte6_kit
  // SRC: https://www.amazon.com/MikroTik-Remote-Cellular-Network-Coverage/dp/B0843RMBLG
  // SRC: https://itamtech.com/mikrotik-sxtr-ep06-a-sxt-lte6-kit-us-9-dbi-60-degrees-lte-antenna.html
  //      SXT LTE6 kit: LTE Cat 6 outdoor CPE pointed at tower with
  //      integrated 9 dBi panel antenna (60° beam width), 300/50 Mbps,
  //      carrier aggregation across multiple LTE bands. Dual Micro-SIM
  //      slots for backup link. QCA9531 650 MHz CPU, 64 MB RAM, 16 MB
  //      flash. 2× Ethernet (port 2 has PoE-out for daisy-chained AP).
  //      18-57 V 802.3af/at PoE-in. SXT rugged outdoor casing,
  //      -40..+60 °C. 3G fallback Cat R7/R8, 2G Class 12.
  {
    id: 'mikrotik-sxt-lte6-kit',
    manufacturer: 'Mikrotik',
    model: 'SXT LTE6 kit',
    category: 'cellular-antenna',
    tagline:
      'SXT LTE6 kit — Cat 6 outdoor pointed-at-tower CPE, 9 dBi 60° panel, dual SIM',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/sxt_lte6_kit',
    specs: {
      technology: 'lte-cat6',
      mimo: '2×2',
      pattern: 'panel',
      beamWidthDeg: 60,
      gainDbi: 9,
      simSlots: 2,
      ipRating: 'IP55', // SXT outdoor casing rating
      operatingTempC: '-40..+60',
    },
  },

  // ═══════════════════════════════════════════════════════════════════════
  // Indoor "Chateau" desktop CPE — Wi-Fi + cellular
  // ═══════════════════════════════════════════════════════════════════════

  // SRC: https://mikrotik.com/product/chateaulte18_ax
  //      Chateau LTE18 ax: LTE Cat 18 (1.2 Gbps / 150 Mbps), 7 FDD bands
  //      + 3 TDD, MIMO 4×4 DL / 1×1 UL, Wi-Fi 6 dual-band (2×2 each band,
  //      AX1800: 574 + 1200 Mbps), 4× GbE + 1× 2.5 GbE, 1× USB-A, 4
  //      built-in LTE antennas + 2× SMA external optional, single Micro
  //      SIM, IPQ-6010 quad-core 1.8 GHz, 1 GB RAM, 19 W max / 16 W idle,
  //      240×156×44 mm desktop.
  {
    id: 'mikrotik-chateau-lte18-ax',
    manufacturer: 'Mikrotik',
    model: 'Chateau LTE18 ax',
    category: 'cellular-antenna',
    tagline:
      'Chateau LTE18 ax — Cat 18 (1.2 Gbps) + Wi-Fi 6 AX1800, 4× GbE + 2.5 GbE, desktop',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/chateaulte18_ax',
    specs: {
      technology: 'lte-cat18',
      bands: 'FDD: 1, 3, 5, 7, 8, 20, 28; TDD: 38, 40, 41',
      mimo: '4×4',
      pattern: 'omni', // 4 built-in antennas + 2× SMA external
      simSlots: 1,
      powerConsumptionW: 19,
    },
  },

  // SRC: https://mikrotik.com/product/chateau_5g_ax (search snippets)
  //      Chateau 5G ax: 5G NSA + 5G SA, MIMO 4×4 on 5G+LTE, 6 built-in
  //      cellular antennas + 2× SMA external + 2× SMA for Wi-Fi extension,
  //      Wi-Fi 6 dual-band 2×2 (Wave 2), broad 5G NSA/SA + LTE band
  //      support (FDD 1-3, 5, 7-8, 18-20, 26, 28, 32; TDD 34, 38-43; 5G
  //      SA TDD 38, 40-41, 77-79).
  {
    id: 'mikrotik-chateau-5g-ax',
    manufacturer: 'Mikrotik',
    model: 'Chateau 5G ax',
    category: 'cellular-antenna',
    tagline:
      'Chateau 5G ax — 5G NSA/SA sub-6 + Wi-Fi 6, MIMO 4×4, 6 internal + 2 external SMA',
    verified: true,
    datasheetUrl: 'https://mikrotik.com/product/chateau_5g_ax',
    specs: {
      technology: '5g-sub6',
      bands:
        'LTE FDD: 1-3, 5, 7-8, 18-20, 26, 28, 32; LTE TDD: 34, 38-43; 5G NSA FDD: 1, 3, 5, 7-8, 20, 28; 5G NSA/SA TDD: 38, 40-41, 77-79',
      mimo: '4×4',
      pattern: 'omni', // 6 built-in + 2× SMA external
      simSlots: 1,
    },
  },
];
